import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AddUserCard } from '@/components/admin/AddUserCard';
import { BetaUserRow, getDisplayName } from '@/components/admin/BetaUserRow';
import { BetaUsersEmptyState } from '@/components/admin/BetaUsersEmptyState';
import { BetaUsersFilterBar } from '@/components/admin/BetaUsersFilterBar';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Layout } from '@/components/Layout';
import {
  type AllowlistEntry,
  useAddAllowlistEntry,
  useAllowlist,
  useRemoveAllowlistEntry,
} from '@/hooks/useAllowlist';

const TOAST_DURATION_MS = 2400;

// Staff-only beta allowlist management (issue #240; design: Beta Users
// Admin). Rendering is gated by the is_admin claim in App.tsx — the
// real enforcement is the API's admin middleware, which 404s the
// /admin/allowlist endpoints for non-admins.
export function BetaUsersPage() {
  const navigate = useNavigate();
  const { entries, isLoading, error, refetch } = useAllowlist();
  const { addEntry } = useAddAllowlistEntry();
  const { removeEntry, isSubmitting: isRemoving } = useRemoveAllowlistEntry();

  const [search, setSearch] = useState('');
  const [toast, setToast] = useState<{ message: string; isError: boolean } | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  function showToast(message: string, isError = false) {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ message, isError });
    toastTimerRef.current = setTimeout(() => setToast(null), TOAST_DURATION_MS);
  }

  const filtered = useMemo(() => {
    const list = entries ?? [];
    const query = search.toLowerCase().trim();
    if (!query) return list;
    return list.filter((entry) => {
      const name = getDisplayName(entry)?.toLowerCase() ?? '';
      return name.includes(query) || entry.email.toLowerCase().includes(query);
    });
  }, [entries, search]);

  // Newest-added first; ISO timestamps compare lexicographically.
  const sorted = useMemo(
    () => [...filtered].sort((a, b) => b.addedAt.localeCompare(a.addedAt)),
    [filtered],
  );

  const existingEmails = useMemo(
    () => (entries ?? []).map((entry) => entry.email.toLowerCase()),
    [entries],
  );

  async function handleAdd(email: string): Promise<string | null> {
    const result = await addEntry(email);
    if (!result.ok) return result.message;
    await refetch();
    showToast(`${email} added to beta`);
    return null;
  }

  async function handleRemove(entry: AllowlistEntry) {
    const label = getDisplayName(entry) ?? entry.email;
    const removed = await removeEntry(entry.id);
    await refetch();
    if (removed) {
      showToast(`${label} removed`);
    } else {
      showToast(`Couldn't remove ${label}`, true);
    }
  }

  return (
    <Layout>
      <div className="mx-auto max-w-[640px] px-4 pb-10" data-testid="beta-users-page">
        <div className="flex items-center gap-2 pb-1 pt-5">
          <button
            type="button"
            onClick={() => void navigate('/me')}
            aria-label="Back to account"
            data-testid="beta-users-back"
            className="-ml-1.5 flex h-8 w-8 items-center justify-center rounded-lg text-wolisho-brown opacity-50 transition-opacity hover:opacity-80"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <line x1="19" y1="12" x2="5" y2="12" />
              <polyline points="12 19 5 12 12 5" />
            </svg>
          </button>
          <span className="text-xs font-bold uppercase tracking-wider text-wolisho-brown/60">
            Admin
          </span>
        </div>

        <h1 className="mb-5 mt-1 text-2xl font-bold text-black">Beta users</h1>

        {isLoading ? (
          <LoadingSpinner label="Loading beta users" testId="beta-users-loading" />
        ) : error ? (
          <p className="text-sm text-black/50" data-testid="beta-users-error">
            Couldn&apos;t load the beta users list. Refresh to try again.
          </p>
        ) : (
          <>
            <AddUserCard existingEmails={existingEmails} onAdd={handleAdd} />

            {(entries?.length ?? 0) > 0 && (
              <BetaUsersFilterBar value={search} onChange={setSearch} count={filtered.length} />
            )}

            <div className="overflow-hidden rounded-xl bg-wolisho-card">
              {sorted.length > 0 ? (
                <div className="py-1">
                  {sorted.map((entry, index) => (
                    <div key={entry.id}>
                      {index > 0 && <div className="mx-4 h-px bg-wolisho-brown/[0.06]" />}
                      <BetaUserRow entry={entry} onRemove={handleRemove} isRemoving={isRemoving} />
                    </div>
                  ))}
                </div>
              ) : (
                <BetaUsersEmptyState isFiltered={search.trim().length > 0} />
              )}
            </div>
          </>
        )}
      </div>

      {toast && (
        <div
          className="pointer-events-none fixed bottom-6 left-1/2 z-50 -translate-x-1/2 whitespace-nowrap rounded-lg bg-wolisho-brown px-5 py-2.5 text-[13px] font-bold text-white"
          // Failures get the assertive live region so screen readers hear
          // them immediately; successes stay polite.
          role={toast.isError ? 'alert' : 'status'}
          data-testid="beta-users-toast"
          // The message interpolates the tester's email/name, so mask its
          // text in session replay (issue #252) like the rows above.
          data-ph-mask
        >
          {toast.message}
        </div>
      )}
    </Layout>
  );
}
